import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { CampaignType, StatsCampaignType } from "@front/m19-api-client";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  DAILY_BUDGET,
  IMPRESSIONS,
  Metric,
  MIN_DAILY_SPEND,
  MONTHLY_BUDGET,
  ROAS,
  TARGET_ACOS,
} from "@front/m19-metrics";
import { AdStatsEx, StrategyEx } from "@front/m19-models";
import { DataSetEventAnnotation } from "@front/m19-models/dataset";
import {
  AccountSelectionService,
  AuthService,
  CurrencyService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  TacosStrategiesService,
  UserSelectionService,
} from "@front/m19-services";
import { ITab, ITabsComponent, MODAL_DATA, ModalOptions } from "@front/m19-ui";
import { mergeAdStatsWithTargetHistory, mergeSeveralDates } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { DateRangeSelectorComponent } from "@m19-board/date-range-selector/date-range-selector.component";
import { ChartData, DataSetConfiguration } from "@m19-board/shared/chart-modal/chart-modal.component";
import { ChartRendererComponent } from "@m19-board/shared/chart-modal/chart-renderer.component";
import { TargetingAsinStatsComponent } from "@m19-board/sponsored-product/sp-page/targeting-asin-stats/targeting-asin-stats.component";
import { StrategyPlacementStatsGridComponent } from "@m19-board/strategies/strategy-placement-stats/strategy-placement-stats-grid.component";
import { combineLatest, map, Observable, of, switchMap } from "rxjs";
import { ActivityEventAnnotationsService } from "../../../activities/activity-event-annotations.service";
import { AudienceStatsComponent } from "../audience-stats/audience-stats.component";
import { BusinessStatsComponent } from "../business-stats/business-stats.component";

export type StrategyStatsInputData = {
  strategy: StrategyEx | undefined;
  strategyIds?: Set<number>; // used for total graph
  isReadOnly: boolean;
  withTacticSection?: boolean;
  withTargetingsSection?: boolean;
  withStrategyConfigMetrics?: boolean;
  notOperatedCampaigns?: Set<StatsCampaignType>;
};

enum StrategyStatsTab {
  STRATEGY = 0,
  TARGETING = 1,
  TACTICS = 2,
  AUDIENCE = 3,
  BUSINESS = 4,
}

@Component({
  template: `<div class="flex h-full flex-col overflow-hidden">
    <ITabs [tabs]="tabs" [(activeTab)]="activeTab" />
    <div class="flex justify-end pt-4 pb-2">
      <app-date-range-selector />
    </div>
    @switch (activeTab()) {
      @case (StrategyStatsTab.STRATEGY) {
        <app-chart-renderer
          class="min-h-0 flex-1"
          [locale]="locale()"
          [currency]="currency()"
          [metrics]="METRIC_COLUMNS"
          [localStorageKey]="localStorageKey"
          [dataSetConfiguration]="dataSetConfiguration"
          [chartData]="chartData$"
          [selectedMetrics]="selectedMetrics"
          [annotations$]="annotations$"
          [strategyTargetHistory]="true"
          [withRangeSelector]="false"
        />
      }
      @case (StrategyStatsTab.TACTICS) {
        <app-strategy-placement-stats-grid
          [strategy]="strategy!"
          [currency]="currency()"
          [locale]="locale()"
          class="flex-1"
        />
      }
      @case (StrategyStatsTab.TARGETING) {
        <app-targeting-asin-stats
          class="flex-1"
          [isReadOnly]="modalData.isReadOnly"
          [strategy]="strategy!"
          [withTOSROSwitch]="withTOSROSwitch"
        />
      }
      @case (StrategyStatsTab.AUDIENCE) {
        <app-audience-stats [strategy]="strategy!" class="flex-1" />
      }
      @case (StrategyStatsTab.BUSINESS) {
        <app-business-stats [strategy]="strategy!" class="flex-1" />
      }
    }
  </div>`,
  imports: [
    ChartRendererComponent,
    TargetingAsinStatsComponent,
    ITabsComponent,
    AudienceStatsComponent,
    StrategyPlacementStatsGridComponent,
    DateRangeSelectorComponent,
    BusinessStatsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyStatsComponent {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "xl", height: "fill" };

  private readonly authService = inject(AuthService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly statsService = inject(StatsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly currencyService = inject(CurrencyService);

  readonly modalData = inject<StrategyStatsInputData>(MODAL_DATA);

  readonly METRIC_COLUMNS: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];
  readonly dataSetConfiguration: DataSetConfiguration<AdStatsEx> = {
    aggregationFunction: [mergeAdStatsWithTargetHistory],
    metricsOnSameScale: [
      [AD_SALES, COST],
      [ACOS, TARGET_ACOS],
      [COST, MIN_DAILY_SPEND, DAILY_BUDGET, MONTHLY_BUDGET],
    ],
  };
  readonly selectedMetrics = [AD_SALES, COST];
  readonly tabs: ITab<StrategyStatsTab>[] = [
    {
      label: this.translocoService.translate("common.strategy"),
      value: StrategyStatsTab.STRATEGY,
    },
  ];
  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.strategy };
  readonly StrategyStatsTab = StrategyStatsTab;

  withTOSROSwitch = false;
  chartData$!: Observable<ChartData<AdStatsEx>>;
  annotations$!: Observable<DataSetEventAnnotation[]>;

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });

  readonly withStrategyConfigMetrics =
    this.modalData.withStrategyConfigMetrics !== undefined ? this.modalData.withStrategyConfigMetrics : true;
  readonly activeTab = signal<StrategyStatsTab>(StrategyStatsTab.STRATEGY);

  readonly strategy: StrategyEx | undefined = undefined;

  private readonly tacosStrategyIndex$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.tacosStrategiesService.getTacosStrategyIndex(am.accountId, am.marketplace)),
  );

  constructor() {
    this.strategy = this.modalData.strategy;

    if (this.modalData.withTacticSection && this.strategy) {
      const label = this.getTacticSectionLabel(this.strategy);
      this.tabs.push({
        label,
        value: StrategyStatsTab.TACTICS,
      });
    }

    if (this.modalData.withTargetingsSection && this.strategy) {
      this.tabs.push({
        label: this.translocoService.translate("targeting-asin-stats.targeting"),
        value: StrategyStatsTab.TARGETING,
      });
    }

    if (this.strategy?.boostAudienceId) {
      this.tabs.push({
        label: this.translocoService.translate("common.audience"),
        value: StrategyStatsTab.AUDIENCE,
      });
    }

    if (this.strategy?.isSpStrategy() && this.strategy.businessBoostEnabled) {
      this.tabs.push({
        label: this.translocoService.translate("amazon-business-boost.title"),
        value: StrategyStatsTab.BUSINESS,
      });
    }

    this.withTOSROSwitch = this.strategy?.campaignType === CampaignType.SP;
    const dailyCampaignStats$ = combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, dr, currency]) =>
        combineLatest([
          this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
          am.dspAdvertiserId
            ? this.statsService.getDailyDspCampaignStats(
                am.accountId,
                am.marketplace,
                am.dspAdvertiserId,
                currency,
                dr[0],
                dr[1],
              )
            : of([]),
        ]),
      ),
      map(([data, dspData]) => [...data, ...dspData]),
    );
    const previousDailyCampaignStats$ = combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, pc, currency]) => {
        if (!pc?.period) {
          return of([]);
        }
        return combineLatest([
          this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, pc.period[0], pc.period[1]),
          am.dspAdvertiserId
            ? this.statsService.getDailyDspCampaignStats(
                am.accountId,
                am.marketplace,
                am.dspAdvertiserId,
                currency,
                pc.period[0],
                pc.period[1],
              )
            : of([]),
        ]).pipe(
          map(([data, dspData]) => {
            return [...data, ...dspData];
          }),
        );
      }),
    );

    this.chartData$ = combineLatest([
      this.tacosStrategyIndex$,
      dailyCampaignStats$,
      previousDailyCampaignStats$,
      combineLatest([
        this.accountSelectionService.singleAccountMarketplaceSelection$,
        this.userSelectionService.dateRange$,
        this.userSelectionService.selectedCurrency$,
      ]).pipe(
        switchMap(([am, dr, currency]) =>
          this.statsService.getStrategyConfigHistory(
            am.accountId,
            am.marketplace,
            currency,
            am.dspAdvertiserId,
            dr[0],
            dr[1],
          ),
        ),
      ),
    ]).pipe(
      map(([tacosStrategyIndex, dailyPlacementStats, previousPeriodDailyPlacementStats, targetHistory]) => {
        const strategyIds = this.modalData.strategyIds ?? new Set([this.strategy?.strategyId]);
        if (this.strategy?.isSpStrategy() && this.strategy.tacosTarget) {
          const relatedTacosStrategy = Array.from(tacosStrategyIndex.values()).find(
            (t) => t.spStrategyId === this.strategy?.strategyId,
          );
          if (relatedTacosStrategy) {
            if (relatedTacosStrategy.sbStrategyId) strategyIds.add(relatedTacosStrategy.sbStrategyId);
            if (relatedTacosStrategy.sdStrategyId) strategyIds.add(relatedTacosStrategy.sdStrategyId);
          }
        }

        const strategyTargetHistory = this.withStrategyConfigMetrics
          ? targetHistory.filter((t) => strategyIds.has(t.strategyId))
          : [];
        const stats = dailyPlacementStats.filter((d) => this.getStatsFilter(d, strategyIds));
        const previousStats = previousPeriodDailyPlacementStats.filter((d) => this.getStatsFilter(d, strategyIds));

        const totalData = stats.reduce((prev, curr) => mergeSeveralDates(prev, curr), {});
        const totalPreviousData = previousStats?.reduce((curr, prev) => mergeSeveralDates(curr, prev), {});
        return {
          data: [...(strategyTargetHistory as any), ...stats],
          previousData: previousStats,
          totalData,
          totalPreviousData,
        };
      }),
    );

    this.annotations$ = this.strategy
      ? this.activityEventAnnotationsService.getStrategyActivityEventAnnotation(this.strategy)
      : of([]);
  }

  private getTacticSectionLabel(strategy: StrategyEx) {
    return strategy.campaignType === CampaignType.SB
      ? this.translocoService.translate("overview-grid.tactic_placement_and_creative_stats_details")
      : strategy.campaignType == CampaignType.SD
        ? this.translocoService.translate("overview-grid.tactic_audience_and_placement_stats_details")
        : this.translocoService.translate("overview-grid.tactic_and_placement_stats_details");
  }

  private getStatsFilter(stats: AdStatsEx, strategyIds: Set<number | undefined>): boolean {
    const notOperatedCampaigns = this.modalData.notOperatedCampaigns;
    if (notOperatedCampaigns !== undefined && stats.strategyId === undefined) {
      return notOperatedCampaigns.has(stats.campaignType!);
    }
    if (stats.strategyId !== undefined) {
      return strategyIds.has(stats.strategyId!);
    } else {
      return false;
    }
  }
}
